import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { AlertCircleIcon, ArrowLeft, Eye, Loader2Icon, ThumbsUpIcon } from "lucide-react";
import { motion } from "motion/react";
import { Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useComplete } from "../hooks/use-complete";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { UserPortalCallout } from "@babackapp/return-widget-components/components/user-portal-callout";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { useWidgetParams } from "../hooks/use-widget-params";
import { CompleteSummary } from "./complete-summary";

export function ReturnRequestComplete() {
  const navigate = useNavigate();
  const { status, returnRequestId, error, confirm } = useComplete();
  const { orderId, shopId } = useWidgetParams();
  const { data: shop } = usePublicShop(shopId);
  const { data: order } = usePublicOrder(orderId);
  const draft = useDraftReturn(orderId);
  const t = useCustomText();

  const rebuyItems = draft?.rebuyItems ?? [];
  const cart = rebuyItems.length > 0
    ? { items: rebuyItems, currency: order?.currency?.shopCurrency ?? "EUR" }
    : null;

  if (status === "pending") {
    return (
      <WidgetPage>
        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mx-auto">
          <Loader2Icon className="size-10 animate-spin text-muted-foreground" />
        </motion.span>
        <Header>
          <HeaderTitle>{t("complete.pending.title")}</HeaderTitle>
          <HeaderDescription>
            {t("complete.pending.description")}
          </HeaderDescription>
        </Header>
      </WidgetPage>
    );
  }

  if (status === "success") {
    return (
      <WidgetPage>
        <motion.span
          initial={{ opacity: 0, scale: 0.9, y: 5, rotate: 30 }}
          animate={{ opacity: 1, scale: 1, rotate: 0, y: 0 }}
          exit={{
            opacity: 0,
            scale: 0.98,
            rotate: -10,
            transition: { duration: 0.25, ease: "easeIn" },
          }}
          transition={{ delay: 0.2, type: "spring" }}
          style={{ transformOrigin: "top left" }}
        >
          <ThumbsUpIcon className="text-success-foreground size-10 mx-auto" />
        </motion.span>
        <Header>
          <HeaderTitle>{t("complete.success.title")}</HeaderTitle>
          <HeaderDescription>
            {t("complete.success.description")}
          </HeaderDescription>
        </Header>
        <UserPortalCallout texts={shop?.preferences?.userPortal?.endProcess} />
        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
            <Link to={`../return-requests/${returnRequestId}`}>
              <Button>
                <Eye />
                {t("complete.success.cta")}
              </Button>
            </Link>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      </WidgetPage>
    );
  }

  if (status === "error") {
    return (
      <WidgetPage>
        <motion.span
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mx-auto"
        >
          <AlertCircleIcon className="text-destructive size-10 mx-auto" />
        </motion.span>
        <Header>
          <HeaderTitle>{t("common.error.title")}</HeaderTitle>
          <HeaderDescription>
            {error?.message ?? t("complete.error.fallback")}
          </HeaderDescription>
        </Header>
        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
            <Button onClick={confirm}>{t("common.retry")}</Button>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      </WidgetPage>
    );
  }

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{t("complete.confirm.title")}</HeaderTitle>
        <HeaderDescription>{t("complete.confirm.description")}</HeaderDescription>
      </Header>
      <Suspense fallback={null}>
        <CompleteSummary orderId={orderId} cart={cart} />
      </Suspense>
      <AnimatedButtonRow variants={{}}>
        <AnimatedButtonContainer layoutId="line-item-back-button" disableVariants={{ exit: true }}>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            <ArrowLeft />
            {t("common.back")}
          </Button>
        </AnimatedButtonContainer>
        <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
          <Button onClick={confirm}>{t("common.confirm")}</Button>
        </AnimatedButtonContainer>
      </AnimatedButtonRow>
    </WidgetPage>
  );
}
